import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  importGedcom,
  ImportResult,
  previewImport,
  ImportPreview,
  listTrees,
  previewMerge,
  applyMerge,
  MergePreview,
  MergeResult,
} from "../../api/trees";
import { useAuth } from "../../hooks/useAuth";
import { useVaultContext } from "../../context/VaultContext";
import LoadingSpinner from "../Common/LoadingSpinner";

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type ImportMode = "create" | "merge";
type ImportStep = "upload" | "modeSelect" | "preview" | "mergePreview" | "importing" | "complete";

export default function ImportDialog({ isOpen, onClose }: ImportDialogProps) {
  const { getAccessTokenSilently } = useAuth0();
  const { getAccessToken } = useAuth();
  const { currentVaultId } = useVaultContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<ImportStep>("upload");
  const [mode, setMode] = useState<ImportMode>("create");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mergePreview, setMergePreview] = useState<MergePreview | null>(null);
  const [result, setResult] = useState<ImportResult | MergeResult | null>(null);
  const [treeName, setTreeName] = useState("");
  const [selectedTreeId, setSelectedTreeId] = useState<number | null>(null);
  const [selectedPersonIds, setSelectedPersonIds] = useState<Set<string>>(new Set());
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch available trees for merge mode (filtered by current vault)
  const { data: trees = [] } = useQuery({
    queryKey: ["trees", currentVaultId],
    queryFn: async () => {
      const token = await getAccessToken();
      return listTrees(token, currentVaultId || undefined);
    },
    enabled: isOpen && mode === "merge" && !!currentVaultId,
  });

  const previewMutation = useMutation({
    mutationFn: async (file: File) => {
      const token = await getAccessTokenSilently();
      return previewImport(file, token);
    },
    onSuccess: (data) => {
      setPreview(data);
      setTreeName(data.tree_name || file?.name.replace(".ged", "") || "");
      setStep("modeSelect");
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to preview file");
    },
  });

  const mergePreviewMutation = useMutation({
    mutationFn: async ({ treeId, file }: { treeId: number; file: File }) => {
      const token = await getAccessTokenSilently();
      return previewMerge(treeId, file, token);
    },
    onSuccess: (data) => {
      setMergePreview(data);
      // Initially select all items
      const allPersonIds = new Set([
        ...data.persons_to_add.map((p) => p.gedcom_id),
        ...data.persons_to_update.map((p) => p.gedcom_id),
      ]);
      const allFamilyIds = new Set([
        ...data.families_to_add.map((f) => f.gedcom_id),
        ...data.families_to_update.map((f) => f.gedcom_id),
      ]);
      setSelectedPersonIds(allPersonIds);
      setSelectedFamilyIds(allFamilyIds);
      setStep("mergePreview");
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to preview merge");
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file selected");
      const token = await getAccessTokenSilently();
      return importGedcom(file, token, treeName || undefined, currentVaultId || undefined);
    },
    onSuccess: async (data) => {
      setResult(data);
      setStep("complete");
      // Invalidate and refetch trees list immediately
      await queryClient.invalidateQueries({ 
        queryKey: ["trees", currentVaultId],
        refetchType: 'all'
      });
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to import file");
      setStep("preview");
    },
  });

  const applyMergeMutation = useMutation({
    mutationFn: async () => {
      if (!file || !selectedTreeId) throw new Error("Missing file or tree selection");
      const token = await getAccessTokenSilently();
      return applyMerge(
        selectedTreeId,
        file,
        {
          person_ids: Array.from(selectedPersonIds),
          family_ids: Array.from(selectedFamilyIds),
        },
        token
      );
    },
    onSuccess: async (data) => {
      setResult(data);
      setStep("complete");
      // Invalidate trees list
      await queryClient.invalidateQueries({
        queryKey: ["trees", currentVaultId],
        refetchType: "all",
      });
      // Invalidate tree detail and person list/count so "X people in tree" and "X people found" stay in sync
      if (data.tree_id) {
        await queryClient.invalidateQueries({ queryKey: ["tree", String(data.tree_id)] });
        await queryClient.invalidateQueries({ queryKey: ["persons", String(data.tree_id)] });
        await queryClient.invalidateQueries({ queryKey: ["personsCount", String(data.tree_id)] });
        await queryClient.invalidateQueries({ queryKey: ["hierarchy", data.tree_id] });
      }
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to apply merge");
      setStep("mergePreview");
    },
  });

  const handleFile = useCallback((selectedFile: File) => {
    if (!selectedFile.name.toLowerCase().endsWith(".ged")) {
      setError("Please select a .ged GEDCOM file");
      return;
    }
    setFile(selectedFile);
    setError(null);
    previewMutation.mutate(selectedFile);
  }, [previewMutation]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleProceedFromModeSelect = () => {
    if (mode === "create") {
      setStep("preview");
    } else {
      if (!selectedTreeId) {
        setError("Please select a tree to merge into");
        return;
      }
      if (file) {
        mergePreviewMutation.mutate({ treeId: selectedTreeId, file });
      }
    }
  };

  const handleImport = () => {
    setStep("importing");
    if (mode === "create") {
      importMutation.mutate();
    } else {
      applyMergeMutation.mutate();
    }
  };

  const togglePersonSelection = (id: string) => {
    const newSet = new Set(selectedPersonIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedPersonIds(newSet);
  };

  const toggleFamilySelection = (id: string) => {
    const newSet = new Set(selectedFamilyIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedFamilyIds(newSet);
  };

  const selectAll = () => {
    if (mergePreview) {
      const allPersonIds = new Set([
        ...mergePreview.persons_to_add.map((p) => p.gedcom_id),
        ...mergePreview.persons_to_update.map((p) => p.gedcom_id),
      ]);
      const allFamilyIds = new Set([
        ...mergePreview.families_to_add.map((f) => f.gedcom_id),
        ...mergePreview.families_to_update.map((f) => f.gedcom_id),
      ]);
      setSelectedPersonIds(allPersonIds);
      setSelectedFamilyIds(allFamilyIds);
    }
  };

  const deselectAll = () => {
    setSelectedPersonIds(new Set());
    setSelectedFamilyIds(new Set());
  };

  const handleClose = () => {
    setStep("upload");
    setMode("create");
    setFile(null);
    setPreview(null);
    setMergePreview(null);
    setResult(null);
    setTreeName("");
    setSelectedTreeId(null);
    setSelectedPersonIds(new Set());
    setSelectedFamilyIds(new Set());
    setError(null);
    onClose();
  };

  const handleViewTree = () => {
    if (result) {
      navigate(`/trees/${result.tree_id}`);
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-vault-400 hover:text-vault-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Upload Step */}
        {step === "upload" && (
          <>
            <h2 className="font-serif text-xl font-semibold text-vault-900">
              Import GEDCOM File
            </h2>
            <p className="mt-2 text-sm text-vault-600">
              Upload a GEDCOM file exported from Ancestry, MyHeritage, FamilySearch,
              or other genealogy software.
            </p>

            <div
              className={`mt-4 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                isDragging
                  ? "border-vault-500 bg-vault-50"
                  : "border-vault-300 hover:border-vault-400"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="mx-auto h-12 w-12 text-vault-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>

              {previewMutation.isPending ? (
                <div className="mt-4">
                  <LoadingSpinner />
                  <p className="mt-2 text-sm text-vault-600">
                    Analyzing file...
                  </p>
                </div>
              ) : (
                <>
                  <p className="mt-4 text-sm text-vault-600">
                    Drag and drop your .ged file here, or
                  </p>
                  <label className="btn-primary mt-2 inline-block cursor-pointer">
                    Browse Files
                    <input
                      type="file"
                      accept=".ged"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </>
              )}
            </div>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </>
        )}

        {/* Mode Selection Step */}
        {step === "modeSelect" && preview && (
          <>
            <h2 className="font-serif text-xl font-semibold text-vault-900">
              Import Mode
            </h2>

            <div className="mt-4 space-y-4">
              <div className="rounded-lg bg-vault-50 p-4">
                <h3 className="font-medium text-vault-900">File Summary</h3>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-vault-600">File:</dt>
                  <dd className="text-vault-900">{preview.filename}</dd>
                  <dt className="text-vault-600">Persons:</dt>
                  <dd className="text-vault-900">{preview.person_count}</dd>
                  <dt className="text-vault-600">Families:</dt>
                  <dd className="text-vault-900">{preview.family_count}</dd>
                </dl>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3 rounded-lg border-2 border-vault-200 p-4 cursor-pointer hover:bg-vault-50 transition-colors has-[:checked]:border-vault-500 has-[:checked]:bg-vault-50">
                  <input
                    type="radio"
                    name="importMode"
                    value="create"
                    checked={mode === "create"}
                    onChange={(e) => setMode(e.target.value as ImportMode)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-vault-900">Create New Tree</div>
                    <div className="text-sm text-vault-600 mt-1">
                      Import as a brand new family tree
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 rounded-lg border-2 border-vault-200 p-4 cursor-pointer hover:bg-vault-50 transition-colors has-[:checked]:border-vault-500 has-[:checked]:bg-vault-50">
                  <input
                    type="radio"
                    name="importMode"
                    value="merge"
                    checked={mode === "merge"}
                    onChange={(e) => setMode(e.target.value as ImportMode)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-vault-900">Merge into Existing Tree</div>
                    <div className="text-sm text-vault-600 mt-1">
                      Add or update data in an existing tree
                    </div>
                  </div>
                </label>
              </div>

              {mode === "merge" && (
                <div>
                  <label className="block text-sm font-medium text-vault-700 mb-2">
                    Select Tree to Merge Into
                  </label>
                  <select
                    value={selectedTreeId || ""}
                    onChange={(e) => setSelectedTreeId(Number(e.target.value) || null)}
                    className="w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                  >
                    <option value="">-- Select a tree --</option>
                    {trees.map((tree) => (
                      <option key={tree.id} value={tree.id}>
                        {tree.name} ({tree.person_count} people)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setStep("upload");
                  setFile(null);
                  setPreview(null);
                }}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                onClick={handleProceedFromModeSelect}
                disabled={mergePreviewMutation.isPending}
                className="btn-primary flex items-center gap-2"
              >
                {mergePreviewMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Loading...
                  </>
                ) : (
                  "Continue"
                )}
              </button>
            </div>
          </>
        )}

        {/* Preview Step (Create Mode) */}
        {step === "preview" && preview && (
          <>
            <h2 className="font-serif text-xl font-semibold text-vault-900">
              Preview Import
            </h2>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-vault-700">
                  Tree Name
                </label>
                <input
                  type="text"
                  value={treeName}
                  onChange={(e) => setTreeName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-vault-300 px-3 py-2 focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                />
              </div>

              <div className="rounded-lg bg-vault-50 p-4">
                <h3 className="font-medium text-vault-900">File Summary</h3>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-vault-600">File:</dt>
                  <dd className="text-vault-900">{preview.filename}</dd>
                  <dt className="text-vault-600">Persons:</dt>
                  <dd className="text-vault-900">{preview.person_count}</dd>
                  <dt className="text-vault-600">Families:</dt>
                  <dd className="text-vault-900">{preview.family_count}</dd>
                  <dt className="text-vault-600">Sources:</dt>
                  <dd className="text-vault-900">{preview.source_count}</dd>
                </dl>
              </div>

              {preview.sample_persons.length > 0 && (
                <div>
                  <h3 className="font-medium text-vault-900">Sample Persons</h3>
                  <ul className="mt-2 space-y-1 text-sm">
                    {preview.sample_persons.map((person) => (
                      <li key={person.gedcom_id} className="text-vault-600">
                        {person.name}
                        {person.birth && ` (b. ${person.birth})`}
                        {person.death && ` - d. ${person.death}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setStep("modeSelect");
                }}
                className="btn-secondary"
              >
                Back
              </button>
              <button onClick={handleImport} className="btn-primary">
                Import Tree
              </button>
            </div>
          </>
        )}

        {/* Merge Preview Step */}
        {step === "mergePreview" && mergePreview && (
          <>
            <h2 className="font-serif text-xl font-semibold text-vault-900">
              Merge Preview
            </h2>

            <div className="mt-4 space-y-4">
              <div className="rounded-lg bg-vault-50 p-4">
                <h3 className="font-medium text-vault-900">Summary</h3>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-vault-600">Total Additions:</dt>
                  <dd className="text-vault-900">{mergePreview.summary.total_additions}</dd>
                  <dt className="text-vault-600">Total Updates:</dt>
                  <dd className="text-vault-900">{mergePreview.summary.total_updates}</dd>
                </dl>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-vault-600 hover:text-vault-900 underline"
                >
                  Select All
                </button>
                <span className="text-vault-400">|</span>
                <button
                  onClick={deselectAll}
                  className="text-sm text-vault-600 hover:text-vault-900 underline"
                >
                  Deselect All
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-4">
                {/* Persons to Add */}
                {mergePreview.persons_to_add.length > 0 && (
                  <div>
                    <h3 className="font-medium text-green-800 mb-2">
                      Persons to Add ({mergePreview.persons_to_add.length})
                    </h3>
                    <div className="space-y-2">
                      {mergePreview.persons_to_add.map((person) => (
                        <label
                          key={person.gedcom_id}
                          className="flex items-start gap-2 text-sm p-2 rounded hover:bg-green-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPersonIds.has(person.gedcom_id)}
                            onChange={() => togglePersonSelection(person.gedcom_id)}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-vault-900">{person.name}</div>
                            <div className="text-xs text-vault-600">
                              {person.birth && `b. ${person.birth}`}
                              {person.birth && person.death && " - "}
                              {person.death && `d. ${person.death}`}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Persons to Update */}
                {mergePreview.persons_to_update.length > 0 && (
                  <div>
                    <h3 className="font-medium text-blue-800 mb-2">
                      Persons to Update ({mergePreview.persons_to_update.length})
                    </h3>
                    <div className="space-y-2">
                      {mergePreview.persons_to_update.map((person) => (
                        <label
                          key={person.gedcom_id}
                          className="flex items-start gap-2 text-sm p-2 rounded hover:bg-blue-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPersonIds.has(person.gedcom_id)}
                            onChange={() => togglePersonSelection(person.gedcom_id)}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-vault-900">{person.name}</div>
                            {person.changes && person.changes.length > 0 && (
                              <div className="text-xs text-vault-600 mt-1">
                                Changes: {person.changes.join(", ")}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Families to Add */}
                {mergePreview.families_to_add.length > 0 && (
                  <div>
                    <h3 className="font-medium text-green-800 mb-2">
                      Families to Add ({mergePreview.families_to_add.length})
                    </h3>
                    <div className="space-y-2">
                      {mergePreview.families_to_add.map((family) => (
                        <label
                          key={family.gedcom_id}
                          className="flex items-start gap-2 text-sm p-2 rounded hover:bg-green-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFamilyIds.has(family.gedcom_id)}
                            onChange={() => toggleFamilySelection(family.gedcom_id)}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-vault-900">
                              Family {family.gedcom_id}
                            </div>
                            {family.spouses && (
                              <div className="text-xs text-vault-600">{family.spouses}</div>
                            )}
                            {family.children && (
                              <div className="text-xs text-vault-600">{family.children}</div>
                            )}
                            {family.marriage && (
                              <div className="text-xs text-vault-600">{family.marriage}</div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Families to Update */}
                {mergePreview.families_to_update.length > 0 && (
                  <div>
                    <h3 className="font-medium text-blue-800 mb-2">
                      Families to Update ({mergePreview.families_to_update.length})
                    </h3>
                    <div className="space-y-2">
                      {mergePreview.families_to_update.map((family) => (
                        <label
                          key={family.gedcom_id}
                          className="flex items-start gap-2 text-sm p-2 rounded hover:bg-blue-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFamilyIds.has(family.gedcom_id)}
                            onChange={() => toggleFamilySelection(family.gedcom_id)}
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium text-vault-900">
                              Family {family.gedcom_id}
                            </div>
                            {family.spouses && (
                              <div className="text-xs text-vault-600">{family.spouses}</div>
                            )}
                            {family.children && (
                              <div className="text-xs text-vault-600">{family.children}</div>
                            )}
                            {family.marriage && (
                              <div className="text-xs text-vault-600">{family.marriage}</div>
                            )}
                            {family.changes && family.changes.length > 0 && (
                              <div className="text-xs text-vault-600 mt-1">
                                Changes: {family.changes.join(", ")}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setStep("modeSelect");
                  setMergePreview(null);
                }}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={selectedPersonIds.size === 0 && selectedFamilyIds.size === 0}
                className="btn-primary"
              >
                Apply Merge ({selectedPersonIds.size + selectedFamilyIds.size} selected)
              </button>
            </div>
          </>
        )}

        {/* Importing Step */}
        {step === "importing" && (
          <div className="py-8 text-center">
            <LoadingSpinner size="lg" />
            <h2 className="mt-4 font-serif text-xl font-semibold text-vault-900">
              Importing Family Tree
            </h2>
            <p className="mt-2 text-vault-600">
              Please wait while we import your family data...
            </p>
          </div>
        )}

        {/* Complete Step */}
        {step === "complete" && result && (
          <>
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-6 w-6 text-green-600"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              </div>
              <h2 className="mt-4 font-serif text-xl font-semibold text-vault-900">
                {mode === "create" ? "Import Complete!" : "Merge Complete!"}
              </h2>
              <p className="mt-2 text-vault-600">
                {mode === "create"
                  ? `Your family tree "${result.tree_name}" has been created.`
                  : `Your tree "${result.tree_name}" has been updated.`}
              </p>
            </div>

            <div className="mt-6 rounded-lg bg-vault-50 p-4">
              <dl className="grid grid-cols-2 gap-2 text-sm">
                {"persons_imported" in result ? (
                  <>
                    <dt className="text-vault-600">Persons imported:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.persons_imported}
                    </dd>
                    <dt className="text-vault-600">Families imported:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.families_imported}
                    </dd>
                    <dt className="text-vault-600">Sources imported:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.sources_imported}
                    </dd>
                  </>
                ) : (
                  <>
                    <dt className="text-vault-600">Persons added:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.persons_added}
                    </dd>
                    <dt className="text-vault-600">Persons updated:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.persons_updated}
                    </dd>
                    <dt className="text-vault-600">Families added:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.families_added}
                    </dd>
                    <dt className="text-vault-600">Families updated:</dt>
                    <dd className="font-medium text-vault-900">
                      {result.families_updated}
                    </dd>
                  </>
                )}
              </dl>
            </div>

            {"warnings" in result && result.warnings.length > 0 && (
              <div className="mt-4 rounded-lg bg-yellow-50 p-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Warnings ({result.warnings.length})
                </h3>
                <ul className="mt-1 list-inside list-disc text-sm text-yellow-700">
                  {result.warnings.slice(0, 5).map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={handleClose} className="btn-secondary">
                Close
              </button>
              <button onClick={handleViewTree} className="btn-primary">
                View Tree
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

